import config from 'lib/config';

interface IUserProperties {
  source_app?: { value: string };
  gav?: { value: string };
  sourceName?: { value: string };
  plan_name?: { value: string };
  client_id?: { value: string };
  client_name?: { value: string };
  client_division?: { value: string };
  community_id?: { value: string };
  community_name?: { value: string };
}

interface IEventParams {
  user_id: string;
  client_id: string;
  non_personalized_ads: boolean;
  events: [];
  user_properties: IUserProperties;
}

interface IParams {
  hitString: string;
  userId: string;
  sessionId: string;
}

class gaFunctions {
  debugMode: boolean;
  param: IParams;
  eventParams: IEventParams;
  sendQueueItemsURL: string[];
  measurementId: string;
  lastTime: number;
  dialogTimer: number;
  moduleId: string;

  constructor() {
    this.debugMode = false;

    this.lastTime = 0;

    this.moduleId = 'na';

    this.dialogTimer = 0;

    this.measurementId = 'G-1F94E6WCTD';

    this.param = {
      /* end point for GA, with credits */
      hitString: 'https://www.google-analytics.com/mp/collect?',
      /* focus 360 user id */
      userId: '',
      sessionId: '',
    };

    this.eventParams = {
      /* non-required, focus user id */
      user_id: '',
      /* GA required, typically uuidv4 */
      client_id: '',
      /* GA required */
      non_personalized_ads: false,
      /* array to send event in, typically cleared per event */
      events: [],
      /* ga user properties, used to store persistent info such as Custom Properties, sent with each hit */
      user_properties: {},
      /* debug var */
    };

    this.sendQueueItemsURL = [];
  }

  private uuidv4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /* client Id is a google tracking id, saved in the users browser */
  private setClientId(newClientId: string): void {
    this.eventParams.client_id = newClientId;

    try {
      localStorage.setItem('f360ga4id', newClientId);
    } catch (e) {}
  }

  private onTimerCheckQue(): void {
    if (this.sendQueueItemsURL.length > 0) {
      fetch(this.sendQueueItemsURL[0], { method: 'POST' }).then((response) => {
        if (response.status.toString() !== '204') {
          console.log(response);
        }
      });
      this.sendQueueItemsURL.shift();
    }
  }

  public newSession(): void {
    this.param.sessionId = Math.floor(new Date().getTime() / 1000).toString();
  }

  /* "web" or "kiosk" */
  public initBasicTracking(sourceApp: string, moduleId: string = 'na'): void {
    /* append api secret to the URL, this determines what account it is tracked to */
    this.param.hitString += 'measurement_id=' + this.measurementId;

    /* init user properties object */
    this.eventParams.user_properties = {
      /* set source app */
      source_app: { value: sourceApp },
      /* set tracking code version */
      gav: { value: config.version.substring(0, 3) },
    };

    /* set module Id for tracking start, mainly for web apps */
    this.moduleId = moduleId;

    /* check local storage for previous focus ga4 tracking */
    let uid = localStorage.getItem('f360ga4id');

    /* not found, generate and store in browser */
    if (uid === null) {
      uid = this.uuidv4();
    }

    /* set this client id on data object */
    this.eventParams.client_id = uid;

    /* initially set user ID to client_id, unless a "login" happens */
    this.setClientId(uid);

    /* init queue array */
    this.sendQueueItemsURL = [];

    /* use set interval to send hits from queue */
    setInterval(() => {
      this.onTimerCheckQue();
    }, 200);

    /* set lastTime starting time for time tracking */
    this.lastTime = Date.now();

    /* record first click.  Many sessions may not have a first click, is shows real engagement */
    document.addEventListener('click', this.firstClick);

    /* add window error tracking */
    window.addEventListener('error', this.onErrorReport);
  }

  public onErrorReport(e: ErrorEvent): void {
    gaFunction.sendEvent('window_error', {
      module_id: this.moduleId,
      error_message: e.message,
      error_file: e.filename,
      error_line: e.lineno,
      error_error: e.error,
    });
  }

  public firstClick(e: Event): void {
    (e.currentTarget as Document).removeEventListener('click', gaFunction.firstClick);
    gaFunction.sendEvent('first_click', {
      module_id: gaFunction.moduleId,
    });
  }

  public setClientInformation(
    focusClientId: string,
    clientName: string,
    clientDivision: string
  ): void {
    /* ensure undefined or null is never set */
    if (focusClientId) {
      this.eventParams.user_properties['client_id'] = {
        value: 'f' + focusClientId,
      };
    }
    if (clientName) {
      this.eventParams.user_properties['client_name'] = {
        value: clientName,
      };
    }
    if (clientDivision) {
      this.eventParams.user_properties['client_division'] = {
        value: clientDivision,
      };
    }
  }

  public setCommunityInformation(communityId: string, communityName: string): void {
    /* ensure "undefined" or null is never set */
    if (communityId) {
      this.eventParams.user_properties['community_id'] = {
        value: communityId,
      };
    }
    if (communityName) {
      this.eventParams.user_properties['community_name'] = {
        value: communityName,
      };
    }
  }

  public resetUser(): void {
    this.setClientId(this.uuidv4());
    this.newSession();
  }

  public newUser(): void {
    this.setClientId(this.uuidv4());
    this.newSession();
  }

  /* user Id is a focus user identifier. should be from log in */
  public setUserId(newUserId: string, method: string = 'na') {
    this.eventParams.user_id = newUserId;
    this.sendEvent('login', { method: method });
  }

  public clearUserId(newUserId: string): void {
    this.eventParams.user_id = newUserId;
    this.sendEvent('logout', {});
  }

  public setSource(sourceName: string): void {
    /* ensure "undefined" or null is never set */
    if (sourceName) {
      this.eventParams.user_properties['sourceName'] = { value: sourceName };
    }
  }

  /* floor plan hits */
  public fpViewPlan(planName: string): void {
    this.moduleId = 'fp';
    this.eventParams.user_properties['plan_name'] = { value: planName };
    this.sendEvent('view_plan', { module_id: 'fp', plan_name: planName });
  }
  public fpFlipPlan(): void {
    this.sendEvent('flip_plan', { module_id: 'fp' });
  }
  public fpReset(): void {
    this.sendEvent('reset', { module_id: 'fp' });
  }
  public fpPrintOpen(): void {
    this.sendEvent('print_open', { module_id: 'fp' });
  }
  public fpPrintCancel(): void {
    this.sendEvent('print_cancel', { module_id: 'fp' });
  }
  public fpPrintStart(layoutSelected: string, showingDimensions: string): void {
    this.sendEvent('print_start', {
      module_id: 'fp',
      selected_layout: layoutSelected,
      showing_dimensions: showingDimensions,
    });
  }
  public fpFindOnSiteMap(): void {
    this.sendEvent('find_on_site_map', { module_id: 'fp' });
  }
  public fpChangeFloorView(floorSelected: string): void {
    this.sendEvent('view_floor', {
      floor_selected: floorSelected,
      module_id: 'fp',
    });
  }
  public fpOpenElevationDialog(): void {
    this.dialogTimer = Date.now();
  }
  public fpViewElevation(elevationStyle: string, isFirst: boolean): void {
    this.sendEvent('view_elevation', {
      elevation_name: elevationStyle,
      is_first: isFirst,
      module_id: 'fp',
    });
  }
  public fpCloseElevationDialog(): void {
    this.sendTiming('dialog', 'Viewing Elevations', Date.now() - this.dialogTimer);
  }
  public fpOpenInteriorStillsDialog(): void {
    this.dialogTimer = Date.now();
  }
  public fpViewInteriorStill(imageTitle: string, isFirst: boolean): void {
    this.sendEvent('view_interior', {
      image_title: imageTitle,
      is_first: isFirst,
      module_id: 'fp',
    });
  }
  public fpCloseInteriorStillsDialog(): void {
    this.sendTiming('dialog', 'Viewing Interior Stills', Date.now() - this.dialogTimer);
  }
  public fpOpenMPVRDialog(): void {
    this.dialogTimer = Date.now();
  }
  public fpCloseMPVRDialog(): void {
    this.sendTiming('dialog', 'Viewing Multipoint VR', Date.now() - this.dialogTimer);
  }
  public fpOpenPivotVRDialog(): void {
    this.dialogTimer = Date.now();
  }
  public fpClosePivotVRDialog(): void {
    this.sendTiming('dialog', 'Viewing Pivot VR', Date.now() - this.dialogTimer);
  }
  public fpOpenLiveFlexDialog(): void {
    this.dialogTimer = Date.now();
  }
  public fpCloseLiveFlexDialog(): void {
    this.sendTiming('dialog', 'Viewing Live Flex', Date.now() - this.dialogTimer);
  }
  public fpTextEditorOpen(): void {
    this.sendEvent('text_editor_open', { module_id: 'fp' });
  }
  public fpTextEditorAddText(): void {
    this.sendEvent('text_editor_add', { module_id: 'fp' });
  }
  public fpTextEditorDeleteText(): void {
    this.sendEvent('text_editor_delete', { module_id: 'fp' });
  }
  public fpSelectOption(optionName: string): void {
    this.sendEvent('select_option', {
      module_id: 'fp',
      option_name: optionName,
    });
  }

  /* site map hits */
  public spSelectLot(lotTitle: string): void {
    this.sendEvent('view_lot', {
      module_id: 'sp',
      lot_title: lotTitle,
    });
  }
  public spViewLotElevation(
    lotTitle: string,
    elevationName: string,
    isFirstElevation: string
  ): void {
    this.sendEvent('view_lot_elevation', {
      module_id: 'sp',
      lot_title: lotTitle,
      elevation_name: elevationName,
      is_first: isFirstElevation,
    });
  }
  public spSelectPlanFromLot(lotTitle: string, planName: string, elevationName: string): void {
    this.eventParams.user_properties['plan_name'] = { value: planName };
    this.sendEvent('select_plan', {
      module_id: 'sp',
      lot_title: lotTitle,
      elevation_name: elevationName,
    });
  }
  public spFilterSelected(filterMenuTitle: string, selectedFilterName: string): void {
    this.sendEvent('filter_selected', {
      module_id: 'sp',
      filter_title: filterMenuTitle,
      selected_filter_name: selectedFilterName,
    });
  }
  public spPrintOpen(): void {
    this.sendEvent('print_open', { module_id: 'sp' });
  }
  public spPrintCancel(): void {
    this.sendEvent('print_cancel', { module_id: 'sp' });
  }
  public spPrintStart(includeLotData: boolean = false): void {
    this.sendEvent('print_start', {
      module_id: 'sp',
      included_lot_data: includeLotData.toString(),
    });
  }
  public spReset(): void {
    this.sendEvent('reset', { module_id: 'sp' });
  }
  public spShowDetails(): void {
    this.sendEvent('show_details', { module_id: 'sp' });
  }
  public spShowDimensions(): void {
    this.sendEvent('show_dimensions', { module_id: 'sp' });
  }

  // vicinity map hits
  public vmSelectPlacemarkMap(markerCategory: string, markerLabel: string): void {
    this.sendEvent('select_marker', {
      module_id: 'vm',
      map_select: true,
      marker_category: markerCategory,
      marker_label: markerLabel,
    });
  }
  public vmSelectPlacemarkMenu(markerCategory: string, markerLabel: string): void {
    this.sendEvent('select_marker', {
      module_id: 'vm',
      map_select: false,
      marker_category: markerCategory,
      marker_label: markerLabel,
    });
  }
  public vmViewPlacemarkDetails(markerCategory: string, markerLabel: string): void {
    this.sendEvent('view_details', {
      module_id: 'vm',
      marker_category: markerCategory,
      marker_label: markerLabel,
    });
  }
  public vmOpenCategoryMenu(markerCategory: string): void {
    this.sendEvent('open_category', {
      module_id: 'vm',
      marker_category: markerCategory,
    });
  }
  public vmUseMapItFeature(): void {
    this.sendEvent('use_map_it', { module_id: 'vm' });
  }

  // plan browser hits
  public pbFilterSelected(filterMenuTitle: string, selectedFilterName: string): void {
    this.sendEvent('filter_selected', {
      module_id: 'pb',
      filter_title: filterMenuTitle,
      selected_filter_name: selectedFilterName,
    });
  }
  public pbSelectPlan(planName: string): void {
    this.eventParams.user_properties['plan_name'] = { value: planName };
    this.sendEvent('select_plan', {
      module_id: 'pb',
    });
  }

  public startSession(): void {
    this.param.sessionId = Math.floor(new Date().getTime() / 1000).toString();

    let hitStr = 'https://www.google-analytics.com/g/collect?v=2&tid=' + this.measurementId;
    hitStr += '&en=session_start&cid=' + this.eventParams.client_id;
    hitStr += '&sid=' + this.param.sessionId;
    if (this.eventParams.user_id !== '') {
      hitStr += '&uid=' + this.eventParams.user_id;
    }
    hitStr += '&sr=' + window.screen.availWidth + 'x' + window.screen.availHeight;
    hitStr += '&_et=100&_s=1&seg=0&ul=' + this.getNavigatorLanguage().toLowerCase();
    hitStr += '&dl=' + encodeURIComponent(document.location.href);
    hitStr += '&dt=' + encodeURIComponent(document.title);

    hitStr += '&_dbg=' + (this.debugMode === true ? '1' : '0');

    let dr = document.referrer === '' ? '' : document.referrer;

    if (this.inIframe() === true) dr = 'iframe';

    if (document.title === 'GA4-Tester') dr = '';
    hitStr += '&dr=' + dr;

    let hit2Str = this.addCustomPerimetersToString(hitStr);

    fetch(hit2Str, { method: 'POST' }).then((response) => {
      if (response.status.toString() !== '204') {
        console.log(response);
      }
    });
  }

  private addCustomPerimetersToString(hitStr: string): string {
    Object.keys(this.eventParams.user_properties).forEach((key) => {
      hitStr +=
        '&up.' +
        key +
        '=' +
        encodeURIComponent(this.eventParams.user_properties[key as keyof IUserProperties]!.value);
    });

    return hitStr;
  }

  private computeDt(): number {
    /* compute time since last event */
    let dt = Date.now() - this.lastTime;
    this.lastTime = Date.now();

    /* if greater than 5 mins from last event, set to 100 */
    if (dt > 5 * 60 * 1000) dt = 100;

    return dt;
  }

  private sendEvent(eventName: string, eventObject: any): void {
    let dt = this.computeDt();

    let hitStr = 'https://www.google-analytics.com/g/collect?v=2&tid=' + this.measurementId;
    hitStr += '&en=' + eventName + '&cid=' + this.eventParams.client_id;
    hitStr += '&sid=' + this.param.sessionId;
    if (this.eventParams.user_id !== '') {
      hitStr += '&uid=' + this.eventParams.user_id;
    }
    /* copy event properties to the ep.* parameters */
    for (const property in eventObject) {
      hitStr += '&ep.' + property + '=' + encodeURIComponent(eventObject[property]);
    }
    hitStr += '&sr=' + window.screen.availWidth + 'x' + window.screen.availHeight;
    hitStr +=
      '&_et=' + dt.toString() + '&_s=1&seg=0&ul=' + this.getNavigatorLanguage().toLowerCase();
    hitStr += '&_dbg=' + (this.debugMode === true ? '1' : '0');

    let hit2Str = this.addCustomPerimetersToString(hitStr);
    this.sendQueueItemsURL.push(hit2Str);
  }

  public sendPageview(pageURL: string): void {
    let dt = this.computeDt();

    let hitStr = 'https://www.google-analytics.com/g/collect?v=2&tid=' + this.measurementId;
    hitStr += '&en=page_view&cid=' + this.eventParams.client_id;
    hitStr += '&sid=' + this.param.sessionId;
    if (this.eventParams.user_id !== '') {
      hitStr += '&uid=' + this.eventParams.user_id;
    }
    //hitStr +=  "&sr="+window.screen.availWidth+"x"+window.screen.availHeight;
    hitStr += '&ep.page_title=' + pageURL;
    hitStr += '&ep.page_location=' + pageURL;
    hitStr +=
      '&_et=' + dt.toString() + '&_s=1&seg=0&ul=' + this.getNavigatorLanguage().toLowerCase();
    hitStr += '&_dbg=' + (this.debugMode === true ? '1' : '0');
    let hit2Str = this.addCustomPerimetersToString(hitStr);
    this.sendQueueItemsURL.push(hit2Str);
  }

  public sendTiming(timingCategory: string, timingName: string, timingValue: number): void {
    let dt = this.computeDt();

    let hitStr = 'https://www.google-analytics.com/g/collect?v=2&tid=' + this.measurementId;
    hitStr += '&en=timing_complete&cid=' + this.eventParams.client_id;
    hitStr += '&sid=' + this.param.sessionId;
    if (this.eventParams.user_id !== '') {
      hitStr += '&uid=' + this.eventParams.user_id;
    }
    hitStr += '&ep.event_label =' + timingName;
    hitStr += '&ep.event_category =' + timingCategory;
    hitStr += '&ep.value=' + timingValue;
    hitStr +=
      '&_et=' + dt.toString() + '&_s=1&seg=0&ul=' + this.getNavigatorLanguage().toLowerCase();

    hitStr += '&_dbg=' + (this.debugMode === true ? '1' : '0');

    let hit2Str = this.addCustomPerimetersToString(hitStr);
    this.sendQueueItemsURL.push(hit2Str);
  }

  private getNavigatorLanguage(): string {
    if (navigator.languages && navigator.languages.length) {
      return navigator.languages[0];
    } else {
      return (
        (navigator as any).userLanguage ||
        navigator.language ||
        (navigator as any).browserLanguage ||
        'en'
      );
    }
  }

  private inIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }
}

const gaFunction = new gaFunctions();

export default gaFunction;
